import React, { useState, useEffect } from "react";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import { useLocation, useNavigate } from "react-router-dom";

const ViewResponse = () => {
  const axiosPrivate = useAxiosPrivate();
  const location = useLocation();
  const navigate = useNavigate();

  const [responseDetails, setResponseDetails] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchResponseDetails = async () => {
      try {
        const searchParams = new URLSearchParams(location.search);
        const quizid = searchParams.get("quizid");
        const participantEmail = searchParams.get("user");

        const response = await axiosPrivate.post(
          "/manager/viewresponse",
          JSON.stringify({
            quizid,
            user: participantEmail,
          })
        );

        setResponseDetails(response.data);
        setError("");
      } catch (err) {
        console.error(err);
        if (err?.response?.data?.message) {
          setError(err.response.data.message);
        } else {
          setError("Failed to fetch response details");
        }
        setResponseDetails(null);
      } finally {
        setLoading(false);
      }
    };

    fetchResponseDetails();
  }, [axiosPrivate, location.search]);

  if (loading) {
    return (
      <div className="fade-in" style={{ textAlign: "center", padding: "4rem" }}>
        <div className="spin" style={{ fontSize: "2rem", marginBottom: "1rem" }}>⏳</div>
        <p style={{ color: "var(--text-secondary)" }}>Loading participant response...</p>
      </div>
    );
  }

  return (
    <div className="fade-in" style={{ maxWidth: "1000px", margin: "0 auto", padding: "2rem 1rem" }}>
      {/* Back Button */}
      <button
        className="btn-secondary"
        style={{ marginBottom: "1.5rem", padding: "0.5rem 1rem", fontSize: "0.875rem" }}
        onClick={() => navigate(-1)}
      >
        ← Back
      </button>

      {error && (
        <div className="errmsg">
          {error}
        </div>
      )}

      {responseDetails && (
        <>
          <div style={{ marginBottom: "2rem", textAlign: "center" }}>
            <h1
              style={{
                fontSize: "2.5rem",
                fontWeight: "700",
                marginBottom: "0.5rem",
                background: "linear-gradient(135deg, var(--primary-purple-light), var(--accent-cyan))",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}
            >
              Participant Response
            </h1>
            <p style={{ color: "var(--text-muted)", fontSize: "1.125rem" }}>
              Detailed breakdown of quiz results
            </p>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: "1.5rem", marginBottom: "2rem" }}>
            {/* Participant Details Card */}
            <div className="card">
              <h2 style={{ fontSize: "1.25rem", fontWeight: "600", marginBottom: "1rem", color: "var(--text-primary)", borderBottom: "1px solid var(--border)", paddingBottom: "0.5rem" }}>
                Participant Details
              </h2>
              <div style={{ display: "grid", gap: "0.75rem" }}>
                <div>
                  <span style={{ color: "var(--text-muted)", fontSize: "0.875rem", textTransform: "uppercase" }}>Name</span>
                  <p style={{ fontWeight: "600", color: "var(--text-primary)" }}>{responseDetails.participantDetails.name}</p>
                </div>
                <div>
                  <span style={{ color: "var(--text-muted)", fontSize: "0.875rem", textTransform: "uppercase" }}>Email</span>
                  <p style={{ fontWeight: "600", color: "var(--text-primary)" }}>{responseDetails.participantDetails.email}</p>
                </div>
                <div style={{ display: "flex", gap: "2rem" }}>
                  <div>
                    <span style={{ color: "var(--text-muted)", fontSize: "0.875rem", textTransform: "uppercase" }}>Phone</span>
                    <p style={{ fontWeight: "600", color: "var(--text-primary)" }}>{responseDetails.participantDetails.phone}</p>
                  </div>
                  <div>
                    <span style={{ color: "var(--text-muted)", fontSize: "0.875rem", textTransform: "uppercase" }}>Age</span>
                    <p style={{ fontWeight: "600", color: "var(--text-primary)" }}>{responseDetails.participantDetails.age}</p>
                  </div>
                  <div>
                    <span style={{ color: "var(--text-muted)", fontSize: "0.875rem", textTransform: "uppercase" }}>Sex</span>
                    <p style={{ fontWeight: "600", color: "var(--text-primary)" }}>{responseDetails.participantDetails.sex}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Scores Card */}
            <div className="card">
              <h2 style={{ fontSize: "1.25rem", fontWeight: "600", marginBottom: "1rem", color: "var(--text-primary)", borderBottom: "1px solid var(--border)", paddingBottom: "0.5rem" }}>
                Assessment Scores
              </h2>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem" }}>
                <div style={{ background: "rgba(124, 58, 237, 0.1)", padding: "1rem", borderRadius: "8px", textAlign: "center", border: "1px solid var(--primary-purple)" }}>
                  <span style={{ fontSize: "2rem", fontWeight: "700", color: "var(--primary-purple-light)" }}>{responseDetails.score[0]}%</span>
                  <p style={{ fontSize: "0.875rem", color: "var(--text-muted)", marginTop: "0.25rem" }}>Factor F</p>
                </div>
                <div style={{ background: "rgba(6, 182, 212, 0.1)", padding: "1rem", borderRadius: "8px", textAlign: "center", border: "1px solid var(--accent-cyan)" }}>
                  <span style={{ fontSize: "2rem", fontWeight: "700", color: "var(--accent-cyan)" }}>{responseDetails.score[1]}%</span>
                  <p style={{ fontSize: "0.875rem", color: "var(--text-muted)", marginTop: "0.25rem" }}>Factor A</p>
                </div>
                <div style={{ background: "rgba(236, 72, 153, 0.1)", padding: "1rem", borderRadius: "8px", textAlign: "center", border: "1px solid var(--accent-pink)" }}>
                  <span style={{ fontSize: "2rem", fontWeight: "700", color: "var(--accent-pink)" }}>{responseDetails.score[2]}%</span>
                  <p style={{ fontSize: "0.875rem", color: "var(--text-muted)", marginTop: "0.25rem" }}>Factor C</p>
                </div>
                <div style={{ background: "rgba(16, 185, 129, 0.1)", padding: "1rem", borderRadius: "8px", textAlign: "center", border: "1px solid var(--success-green)" }}>
                  <span style={{ fontSize: "2rem", fontWeight: "700", color: "var(--success-green)" }}>{responseDetails.score[3]}%</span>
                  <p style={{ fontSize: "0.875rem", color: "var(--text-muted)", marginTop: "0.25rem" }}>Factor E</p>
                </div>
              </div>
            </div>
          </div>

          <div className="card">
            <h2 style={{ fontSize: "1.5rem", fontWeight: "600", marginBottom: "1.5rem", color: "var(--text-primary)" }}>
              Detailed Responses
            </h2>

            <div style={{ display: "flex", flexDirection: "column", gap: "2rem" }}>
              {responseDetails.questions.map((question, index) => (
                <div key={index} style={{ paddingBottom: "1.5rem", borderBottom: index !== responseDetails.questions.length - 1 ? "1px solid var(--border)" : "none" }}>
                  <h3 style={{ fontSize: "1.125rem", fontWeight: "500", marginBottom: "1rem", color: "var(--text-primary)" }}>
                    <span style={{ color: "var(--primary-purple-light)", marginRight: "0.5rem" }}>{index + 1}.</span>
                    {question.questionText}
                  </h3>

                  <div style={{ display: "grid", gap: "0.5rem" }}>
                    {question.options.map((option, optionIndex) => (
                      <div
                        key={optionIndex}
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          padding: "0.75rem 1rem",
                          borderRadius: "8px",
                          background: "rgba(15, 23, 42, 0.3)",
                          border: "1px solid var(--border)"
                        }}
                      >
                        <span style={{ color: "var(--text-secondary)" }}>
                          <span style={{ fontWeight: "600", marginRight: "0.5rem", color: "var(--text-muted)" }}>{option.character}</span>
                          {option.optionText}
                        </span>
                        <span style={{
                          fontWeight: "600",
                          color: "var(--accent-cyan)",
                          background: "rgba(6, 182, 212, 0.1)",
                          padding: "0.1rem 0.5rem",
                          borderRadius: "4px"
                        }}>
                          {option.response}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ViewResponse;
